import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import "./PurposalList.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function ProposalList() {
  const [proposals, setProposals] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [search, setSearch] = useState(location.state?.search || "");

  useEffect(() => {
    if (location.state?.search !== undefined) {
      setSearch(location.state.search);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchProposals = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/getAllProposal`);
        setProposals(res.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchProposals();
  }, []);

  const handleApprove = async (id) => {
    if (!canDo(user, "proposals", "edit"))
      return alert("You do not have permission to approve proposals.");

    try {
      await axios.patch(`${API_URL}/api/approvalproposal/${id}`, {
        status: "Accepted",
      });

      setProposals((prev) =>
        prev.map((p) => (p._id === id ? { ...p, status: "Accepted" } : p))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!canDo(user, "proposals", "delete"))
      return alert("You do not have permission to delete proposals.");

    if (!window.confirm("Are you sure you want to delete this proposal?")) return;

    try {
      await axios.delete(`${API_URL}/api/DeleteProposal/${id}`);
      setProposals(proposals.filter((p) => p._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditClick = (proposal) => {
    navigate(`/admin/updateProposal/${proposal._id}`);
  };

  const statusClass = (status) => {
    switch (status) {
      case "Accepted":
        return "status-accepted";
      case "Sent":
        return "status-sent";
      case "Rejected":
        return "status-rejected";
      case "Draft":
        return "status-draft";
      default:
        return "status-other";
    }
  };

  const downloadExcel = () => {
    const excelData = proposals.map((p, i) => ({
      ID: i + 1,
      Client: p.clientId?.leadName || "N/A",
      Title: p.title,
      Services: p.services?.map((s) => s.name || s.serviceName).join(", "),
      "Project Description": p.description,
      "Company Description": p.companyDescription || "",
      TotalPrice: p.services?.reduce((sum, s) => sum + (s.price || 0), 0),
      Terms: p.terms,
      Status: p.status,
      EnrollDate: new Date(p.createdAt).toLocaleDateString(),
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Proposals");
    XLSX.writeFile(wb, "Proposals_List.xlsx");
  };

  if (!canViewPage(user, "proposals")) {
    return (
      <div className="container py-5 text-center">
        <h2 className="text-danger fw-bold">Access Denied</h2>
        <p>You do not have permission to view proposals.</p>
      </div>
    );
  }

  const filteredProposals = proposals.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.title?.toLowerCase().includes(q) ||
      p.clientId?.leadName?.toLowerCase().includes(q) ||
      p.status?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.companyDescription?.toLowerCase().includes(q) ||
      p.services?.some((s) => (s.name || s.serviceName)?.toLowerCase().includes(q))
    );
  });

  return (
    <div className="proposal-page">
      <div className="proposal-shell">
        <div className="proposal-hero">
          <div>
            <p className="proposal-eyebrow">Sales Pipeline</p>
            <h2>Proposal List</h2>
            <span>Review proposals, terms, approval state, and sending flow</span>
          </div>

          <button className="proposal-download-btn" onClick={downloadExcel}>
            Download Excel
          </button>
        </div>

        <div className="proposal-search-card">
          <input
            type="text"
            className="proposal-search-input"
            placeholder="Search by title, client, service, status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="proposal-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Client</th>
                  <th>Title</th>
                  <th>Services</th>
                  <th>Project Description</th>
                  <th>Company Description</th>
                  <th>Price</th>
                  <th>Terms</th>
                  <th>Status</th>
                  <th>Enrollment Date</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredProposals.length > 0 ? (
                  filteredProposals.map((proposal, index) => (
                    <tr key={proposal._id}>
                      <td>{index + 1}</td>
                      <td>{proposal.clientId?.leadName}</td>
                      <td>{proposal.title}</td>

                      <td>
                        {proposal.services?.length
                          ? proposal.services.map((s) => s.name || s.serviceName).join(", ")
                          : "-"}
                      </td>

                      <td className="text-truncate" style={{ maxWidth: "220px" }} title={proposal.description}>
                        {proposal.description}
                      </td>

                      <td className="text-truncate" style={{ maxWidth: "220px" }} title={proposal.companyDescription}>
                        {proposal.companyDescription}
                      </td>

                      <td>{proposal.services?.reduce((sum, s) => sum + (s.price || 0), 0)}</td>

                      <td className="text-truncate" style={{ maxWidth: "190px" }} title={proposal.terms}>
                        {proposal.terms}
                      </td>

                      <td>
                        <span className={`proposal-status-chip ${statusClass(proposal.status)}`}>
                          {proposal.status}
                        </span>
                      </td>

                      <td>{new Date(proposal.createdAt).toLocaleDateString()}</td>

                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          {canDo(user, "proposals", "edit") && (
                            <>
                              <button
                                className="btn btn-sm proposal-btn edit"
                                onClick={() => handleEditClick(proposal)}
                              >
                                Edit & Send
                              </button>

                              <button
                                className="btn btn-sm proposal-btn approve"
                                disabled={proposal.status === "Accepted" || proposal.status === "Rejected"}
                                onClick={() => handleApprove(proposal._id)}
                              >
                                Approve
                              </button>
                            </>
                          )}

                          {canDo(user, "proposals", "delete") && (
                            <button
                              className="btn btn-sm proposal-btn delete"
                              onClick={() => handleDelete(proposal._id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="proposal-empty-cell">
                      No proposals found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProposalList;
